<template>
  <v-card flat>
    <v-row no-gutters :class="{ 'pl-4': !isThumbButton }">
      <v-col cols="12" v-if="!isThumbButton">
        <v-row no-gutters>
          <v-btn color="primary" icon @click="handleGetBack">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="text-h6" style="vertical-align: middle">
            {{ $t('message.pages.video.buttonsPage.title') }}
          </span>
          <v-spacer />
          <v-btn
            v-if="!isEditing && hasEditVideoPerm"
            class="mr-4"
            color="primary"
            @click="handleEditCta"
            >{{ $t('message.add') }}</v-btn
          >
        </v-row>
      </v-col>
      <v-col cols="12" class="mt-2 ml-2" v-if="!isEditing && !isThumbButton">
        <v-card
          v-scroll.self="onScroll"
          class="overflow-y-auto"
          flat
          max-height="240px"
          width="100%"
        >
          <v-row no-gutters>
            <v-col cols="12" v-for="(cta, i) in allCtasVideo" :key="i">
              <v-card class="mt-2 pl-2 mr-6" outlined height="45px">
                <v-row class="fill-height" no-gutters align="center">
                  <span v-if="!cta.metadata.isCustomHtml">{{ cta.title }}</span>
                  <v-card v-else flat color="rgb(227 232 238)">
                    <span
                      class="font-weight-medium px-2"
                      style="font-size: 12px"
                    >
                      {{
                        $t('message.pages.videoPlayerButtons.customizedHtml')
                      }}
                    </span>
                  </v-card>
                  <v-spacer />
                  <v-progress-circular
                    v-if="loadings.removingCta && beingRemovedCtaId === cta.id"
                    class="mr-2"
                    indeterminate
                    color="primary"
                    size="22"
                    width="3"
                  ></v-progress-circular>
                  <v-btn
                    v-if="
                      cta.metadata.selectedButtonType === 'underVideo' ||
                      (cta.metadata.selectedButtonType === 'insideVideo' &&
                        cta.metadata.appendWebsiteQueryParams)
                    "
                    class="mr-1"
                    icon
                    @click="handleOpenCodeModal(cta)"
                  >
                    <v-icon>mdi-xml</v-icon>
                  </v-btn>
                  <v-menu bottom left offset-y v-if="hasEditVideoPerm">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mr-2" icon v-bind="attrs" v-on="on">
                        <v-icon>mdi-dots-horizontal</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense class="pa-0" width="120px">
                      <v-list-item
                        v-if="
                          cta.metadata.selectedButtonType === 'underVideo' ||
                          (cta.metadata.selectedButtonType === 'insideVideo' &&
                            cta.metadata.appendWebsiteQueryParams)
                        "
                        @click="handleOpenCodeModal(cta)"
                      >
                        <v-list-item-title>{{
                          $t('message.pages.videoPlayerButtons.copyCode')
                        }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="handleEditCta(cta)">
                        <v-list-item-title>{{
                          $t('message.pages.videoPlayerButtons.edit')
                        }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="handleRemoveCta(cta)">
                        <v-list-item-title class="error--text">{{
                          $t('message.pages.videoEditModal.delete')
                        }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col cols="12" class="ml-2 mt-2" v-else>
        <v-row no-gutters class="pr-4">
          <v-row no-gutters v-if="!isThumbButton">
            <v-col cols="12">
              <span>{{
                $t('message.pages.videoPlayerButtons.selectBtnType')
              }}</span>
            </v-col>
            <v-col cols="12" class="mt-4">
              <v-card
                width="100%"
                height="38px"
                flat
                style="border: 1px solid grey"
              >
                <v-row no-gutters>
                  <v-col cols="6">
                    <v-btn
                      class="px-0"
                      block
                      :plain="editingButton.selectedButtonType != 'underVideo'"
                      tile
                      style="border-right: 1px solid grey"
                      :color="
                        editingButton.selectedButtonType === 'underVideo'
                          ? 'primary'
                          : ''
                      "
                      @click="handleChangeButtonType('underVideo')"
                    >
                      <span>{{
                        $t('message.pages.videoPlayerButtons.outsideVideo')
                      }}</span>
                    </v-btn>
                  </v-col>
                  <v-col cols="6">
                    <v-btn
                      class="px-0"
                      block
                      :plain="editingButton.selectedButtonType != 'insideVideo'"
                      tile
                      style="border-right: 1px solid grey"
                      :color="
                        editingButton.selectedButtonType === 'insideVideo'
                          ? 'primary'
                          : ''
                      "
                      @click="handleChangeButtonType('insideVideo')"
                    >
                      <span>{{
                        $t('message.pages.videoPlayerButtons.insideVideo')
                      }}</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="editingButton.isCustomHtml"
                inset
                :label="$t('message.pages.videoPlayerButtons.customizedHtml')"
                dense
                hide-details
                @change="setIframeConfig"
              ></v-switch>
            </v-col>
            <v-row no-gutters class="mt-6" v-if="editingButton.isCustomHtml">
              <v-col cols="12" class="mb-2">
                {{ $t('message.pages.videoPlayerButtons.htmlCode') }}
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editingButton.codeHtml"
                  :placeholder="$t('message.pages.video.buttonHtmlText')"
                  outlined
                  hide-details
                />
              </v-col>
            </v-row>
          </v-row>
          <v-col
            v-if="
              editingButton.selectedButtonType === 'insideVideo' &&
              !editingButton.isCustomHtml
            "
            cols="12"
            class="mt-4"
          >
            <v-row no-gutters align="center">
              <v-card flat height="42" width="42">
                <v-row no-gutters>
                  <v-col
                    cols="4"
                    v-for="placement in buttonPlacements"
                    :key="placement.type"
                  >
                    <v-card
                      height="12px"
                      width="12px"
                      flat
                      class="rounded-circle"
                      :class="
                        editingButton.selectedButtonPlacement ===
                          placement.type && 'primary'
                      "
                      style="margin-top: 2px; border: 0.5px solid grey"
                      @click="handleChangeButtonPlacement(placement.type)"
                    ></v-card>
                  </v-col>
                </v-row>
              </v-card>
              <span class="mt-1 ml-2">{{
                $t('message.pages.videoPlayerButtons.btnPosition')
              }}</span>
            </v-row>
          </v-col>
          <v-col cols="12" class="mt-4" v-if="!editingButton.isCustomHtml">
            <v-row no-gutters>
              <v-col cols="6" class="pr-2">{{
                $t('message.pages.videoPlayerButtons.text')
              }}</v-col>
              <v-col cols="6" class="pl-2">
                {{ $t('message.pages.videoPlayerButtons.size') }}
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" v-if="!editingButton.isCustomHtml">
            <v-row no-gutters>
              <v-col cols="6" class="pr-2">
                <v-text-field
                  v-model="editingButton.title"
                  dense
                  outlined
                  hide-details
                  :placeholder="$t('message.pages.videoPlayerButtons.text')"
                  @input="setIframeConfig"
                ></v-text-field>
              </v-col>
              <v-col cols="6" class="pl-2">
                <v-select
                  v-model="editingButton.selectedButtonSize"
                  :items="buttonSizes"
                  item-value="type"
                  item-text="text"
                  dense
                  outlined
                  hide-details
                  :placeholder="$t('message.pages.videoPlayerButtons.size')"
                  @change="setIframeConfig"
                ></v-select>
              </v-col>
            </v-row>
          </v-col>
          <v-row no-gutters v-if="!editingButton.isCustomHtml">
            <v-col cols="12" class="mt-3"> Link </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editingButton.link"
                dense
                outlined
                hide-details
                :placeholder="
                  $t('message.pages.videoPlayerButtons.linkPlaceholder')
                "
                @change="setIframeConfig"
              ></v-text-field>
            </v-col>
            <v-col v-if="checkDomainRegex" cols="12">
              <span class="red--text caption align-text">
                <v-icon color="red" size="16" class="pb-1 pt-1 pr-1">
                  mdi-alert-circle
                </v-icon>
                {{ $t('message.pages.folderSettings.errors.invalidHostname') }}
              </span>
            </v-col>

            <v-col cols="12" class="mt-3 d-flex">
              <v-switch
                class="ma-0 pa-0 mr-1"
                v-model="editingButton.appendWebsiteQueryParams"
                inset
                :label="
                  $t('message.pages.videoPlayerButtons.appendParentWindowLink')
                "
                dense
                hide-details
              ></v-switch>
              <LearnMoreBtn :onClickBtn="handleOpenAppendArticle" />
            </v-col>
          </v-row>
          <v-row no-gutters v-if="!isThumbButton">
            <v-col cols="12" class="mt-2">
              <v-row no-gutters>
                <v-col cols="6" class="pr-2">{{
                  $t('message.pages.videoPlayerButtons.start')
                }}</v-col>
                <v-col cols="6" class="pl-2">{{
                  $t('message.pages.videoPlayerButtons.end')
                }}</v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-row no-gutters>
                <v-col cols="6" class="pr-2">
                  <!-- start -->
                  <AntTimePicker
                    :startVal="editingButton.startTime"
                    :timeVideo="timeVideo"
                    :currentVal="editingButton.startTime"
                    :disabled="editingButton.lastAllVideo"
                    @changedTime="
                      (time) =>
                        (editingButton.startTime = time) && setIframeConfig()
                    "
                  />
                </v-col>
                <v-col cols="6" class="pl-2">
                  <!-- end -->
                  <AntTimePicker
                    :key="
                      isLowerOrEqual
                        ? editingButton.startTime
                        : editingButton.endTime
                    "
                    :startVal="
                      isLowerOrEqual
                        ? editingButton.startTime
                        : editingButton.endTime
                    "
                    :thresholdTime="minEndTime"
                    :timeVideo="timeVideo"
                    :currentVal="editingButton.endTime"
                    :disabled="
                      editingButton.lastAllVideo || editingButton.lastTillEnd
                    "
                    @changedTime="
                      (time) =>
                        (editingButton.endTime = time) && setIframeConfig()
                    "
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" class="mt-2">
              <v-row no-gutters>
                <v-col cols="6" class="pr-2">
                  <v-checkbox
                    v-model="editingButton.lastAllVideo"
                    :label="$t('message.pages.videoPlayerButtons.lastAllVideo')"
                    class="pa-0 ma-0"
                    color="primary"
                    hide-details
                    @click="handleLastAllVideo"
                  ></v-checkbox>
                </v-col>
                <v-col cols="6" class="pl-2">
                  <v-checkbox
                    v-model="editingButton.lastTillEnd"
                    :label="$t('message.pages.videoPlayerButtons.lastTillEnd')"
                    dense
                    class="pa-0 ma-0"
                    color="primary"
                    hide-details
                    @click="handleLastTillEnd"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-col>
            <!-- <v-col cols="12" class="mt-2">
              <v-checkbox
                v-model="editingButton.keepWindow"
                :label="$t('message.pages.videoPlayerButtons.keepWindow')"
                class="pa-0 ma-0"
                color="primary"
                hide-details
                @click="handleKeepWindow"
              ></v-checkbox>
            </v-col> -->
          </v-row>
          <v-row no-gutters v-if="!editingButton.isCustomHtml">
            <v-col cols="12" class="mt-3 mb-2">
              <span class="font-weight-bold">{{
                $t('message.pages.videoPlayerButtons.colors')
              }}</span>
            </v-col>
            <v-col cols="12">
              <v-row no-gutters>
                <v-col cols="6" class="pr-2">
                  <v-row no-gutters>
                    <v-col cols="12" class="mb-1">
                      <span class="h6">
                        {{ $t('message.pages.videoPlayerButtons.textColor') }}
                      </span>
                    </v-col>
                    <v-col cols="12">
                      <v-menu
                        v-model="textColorMenu"
                        offset-x
                        top
                        nudge-bottom="105"
                        nudge-left="16"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :color="editingButton.textColor"
                            v-on="on"
                            :style="setTextColorMenu"
                            icon
                            elevation="3"
                          ></v-btn>
                        </template>
                        <v-color-picker
                          elevation="3"
                          v-model="editingButton.textColor"
                          flat
                          mode="hexa"
                          hide-mode-switch
                          :maxlength="7"
                          @input="setIframeConfig"
                        ></v-color-picker>
                      </v-menu>
                      <span class="ml-2">{{ editingButton.textColor }}</span>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6" class="pl-2">
                  <v-row no-gutters>
                    <v-col cols="12" class="mb-1">
                      <span class="h6"> Background </span>
                    </v-col>
                    <v-col cols="12">
                      <v-menu
                        v-model="backgroundColorMenu"
                        offset-x
                        top
                        nudge-bottom="105"
                        nudge-left="16"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :color="editingButton.backgroundColor"
                            v-on="on"
                            :style="setBackgroundColorMenu"
                            icon
                            elevation="3"
                          ></v-btn>
                        </template>
                        <v-color-picker
                          elevation="3"
                          v-model="editingButton.backgroundColor"
                          flat
                          mode="hexa"
                          hide-mode-switch
                          :maxlength="7"
                          @input="setIframeConfig"
                        ></v-color-picker>
                      </v-menu>
                      <span class="ml-2">{{
                        editingButton.backgroundColor
                      }}</span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" class="mt-4">
              <v-row no-gutters>
                <v-col cols="6" class="pr-2">
                  <v-row no-gutters>
                    <v-col cols="12" class="mb-1">
                      <span class="h6">
                        {{ $t('message.pages.videoPlayerButtons.textColor') }}
                        Hover
                      </span>
                    </v-col>
                    <v-col cols="12">
                      <v-menu
                        v-model="textHoverMenu"
                        offset-x
                        top
                        nudge-bottom="105"
                        nudge-left="16"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :color="editingButton.textHover"
                            v-on="on"
                            :style="setTextHoverColorMenu"
                            icon
                            elevation="3"
                          ></v-btn>
                        </template>
                        <v-color-picker
                          elevation="3"
                          v-model="editingButton.textHover"
                          flat
                          mode="hexa"
                          hide-mode-switch
                          :maxlength="7"
                          @input="setIframeConfig"
                        ></v-color-picker>
                      </v-menu>
                      <span class="ml-2">{{ editingButton.textHover }}</span>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="6" class="pl-2">
                  <v-row no-gutters>
                    <v-col cols="12" class="mb-1">
                      <span class="h6"> Background Hover </span>
                    </v-col>
                    <v-col cols="12">
                      <v-menu
                        v-model="backgroundHoverMenu"
                        offset-x
                        top
                        nudge-bottom="105"
                        nudge-left="16"
                        :close-on-content-click="false"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn
                            :color="editingButton.backgroundHover"
                            v-on="on"
                            :style="setBackgroundHoverColorMenu"
                            icon
                            elevation="3"
                          ></v-btn>
                        </template>
                        <v-color-picker
                          elevation="3"
                          v-model="editingButton.backgroundHover"
                          flat
                          mode="hexa"
                          hide-mode-switch
                          :maxlength="7"
                          @input="setIframeConfig"
                        ></v-color-picker>
                      </v-menu>
                      <span class="ml-2">{{
                        editingButton.backgroundHover
                      }}</span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row
            no-gutters
            v-if="
              editingButton.selectedButtonType === 'underVideo' &&
              !editingButton.isCustomHtml &&
              !isThumbButton
            "
          >
            <v-col cols="12" class="mt-6">
              {{ $t('message.pages.videoPlayerButtons.preview') }}
            </v-col>
            <v-col cols="12" class="mt-1">
              <v-divider />
            </v-col>
            <v-col cols="12">
              <v-row align="center" justify="center">
                <span v-html="generateInsideButtonScript('outsideBtn')"></span>
              </v-row>
            </v-col>
          </v-row>
          <v-col
            cols="12"
            class="d-flex flex-row align-end mt-8"
            v-if="!isThumbButton && hasEditVideoPerm"
          >
            <v-spacer />
            <v-btn min-width="150" plain @click="handleCancelEditing">{{
              $t('message.pages.videoPlayerButtons.cancel')
            }}</v-btn>
            <v-btn
              min-width="150"
              color="primary"
              :disabled="
                loadings.creatingCta || loadings.updatingCta || editBtnChanged
              "
              :loading="loadings.creatingCta || loadings.updatingCta"
              @click="handlePublishNewCta"
              >{{ $t('message.pages.videoPlayerButtons.publish') }}</v-btn
            >
          </v-col>
        </v-row>
      </v-col>
      <span v-if="error" class="red--text caption align-text mt-4 ml-2">
        <v-icon color="red" size="16" class="pb-1 pt-1 pr-1">
          mdi-alert-circle
        </v-icon>
        {{ error }}
      </span>
    </v-row>
    <unsaved-changes-modal />
    <cta-modal />
  </v-card>
</template>
<script>
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import { mapMutations, mapState } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import playerApi from '@/services/playerApi'
import CtaModal from './CtaModal.vue'
import AntTimePicker from '@/components/AntTimePicker.vue'
import LearnMoreBtn from '@/components/LearnMoreBtn.vue'

export default {
  name: 'VideoPlayerButtons',
  components: {
    UnsavedChangesModal,
    CtaModal,
    AntTimePicker,
    LearnMoreBtn,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    isThumbButton: {
      type: Boolean,
      default: false,
    },
    hasEdit: {
      type: Array,
    },
  },
  data: () => ({
    isEditing: false,
    timeVideo: null,
    buttonType: 'video',
    editingButton: {
      title: '',
      link: '',
      startTime: '00:00',
      endTime: '00:05',
      selectedButtonType: 'underVideo',
      textColor: '#FFFFFF',
      textHover: '#FFFFFF',
      backgroundColor: '#3e50f7',
      backgroundHover: '#3e50f7',
      selectedButtonSize: 'small',
      selectedButtonPlacement: 'bottomCenter',
      isCustomHtml: false,
      codeHtml: '',
      lastAllVideo: false,
      lastTillEnd: false,
      keepWindow: false,
      appendWebsiteQueryParams: false,
    },
    ctaButtons: [],
    outsideCtaButtons: [],
    textColorMenu: false,
    textHoverMenu: false,
    backgroundColorMenu: false,
    backgroundHoverMenu: false,
    buttonSizes: null,
    buttonPlacements: [
      { type: 'topLeft', text: 'Topo a esquerda' },
      { type: 'topCenter', text: 'Topo centralizado' },
      { type: 'topRight', text: 'Topo a direita' },
      { type: 'midLeft', text: 'Meio a esquerda' },
      { type: 'midCenter', text: 'Meio centralizado' },
      { type: 'midRight', text: 'Meio a direita' },
      { type: 'bottomLeft', text: 'Fundo a esquerda' },
      { type: 'bottomCenter', text: 'Fundo centralizado' },
      { type: 'bottomRight', text: 'Fundo a direita' },
    ],
    scrollInvoked: 0,
    beingRemovedCtaId: '',
    beingEditedCtaId: '',
    loadings: {
      creatingCta: false,
      updatingCta: false,
      removingCta: false,
    },
    error: '',
    wasSomethingChanged: false,
    initialEditingCtaValue: {},
    pauseThumbUrl: '',
    initialAppendWebsite: false,
  }),
  computed: {
    ...mapState({
      video: (state) => state.video.video,
    }),
    editBtnChanged() {
      if (
        this.initialAppendWebsite !==
        this.editingButton.appendWebsiteQueryParams
      )
        return false
      return !this.wasSomethingChanged
    },
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    setTextColorMenu() {
      return {
        backgroundColor: this.editingButton.textColor,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setTextHoverColorMenu() {
      return {
        backgroundColor: this.editingButton.textHover,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setBackgroundColorMenu() {
      return {
        backgroundColor: this.editingButton.backgroundColor,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setBackgroundHoverColorMenu() {
      return {
        backgroundColor: this.editingButton.backgroundHover,
        borderRadius: '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    allCtas() {
      return this.ctaButtons.concat(this.outsideCtaButtons)
    },
    allCtasVideo() {
      return this.allCtas.filter(
        (cta) =>
          cta.type !== 'end' &&
          cta.type !== 'pause' &&
          !cta?.metadata?.isMiniTrigger
      )
    },
    checkDomainRegex() {
      let link = this.editingButton.link ?? ''
      if (link.length < 2) {
        return false
      }
      const pattern = new RegExp(
        /^(https?):\/\/([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/\S*)?$/
      )
      return !pattern.test(link)
    },
    isLowerOrEqual() {
      const { endTime, startTime } = this.editingButton
      return endTime < startTime
    },
    minEndTime() {
      return this.blockingEndTime()
    },
  },
  mounted() {
    this.editingButton.title = this.$t('message.pages.video.buttonDefaultText')
    this.buttonSizes = [
      { type: 'small', text: this.$t('message.pages.video.small') },
      { type: 'medium', text: this.$t('message.pages.video.medium') },
      { type: 'large', text: this.$t('message.pages.video.big') },
    ]
    this.timeVideo = this.$store.state.video.video?.length
    this.$root.$on('discardChangesBtnPage', () => {
      this.wasSomethingChanged = false
      this.setConfigCtas()
      this.handleGetBack()
    })
    this.setConfigCtas()
    if (this.isThumbButton) {
      this.editingButton.selectedButtonType = 'insideVideo'
    }
    this.$root.$on('getPauseThumbButton', () => {
      this.handleThumbnailButton()
    })
    this.$root.$on('editPauseThumbButton', (buttonConfig, pauseThumb = '') => {
      this.editingButton = buttonConfig
      this.fixAppendWebsiteVal()
      this.pauseThumbUrl = pauseThumb
      this.buttonType = 'pause'
      this.setIframeConfig()
    })
    this.isEditing = this.allCtasVideo.length === 0
    this.hasEdit.push(this.wasSomethingChanged)
  },
  beforeDestroy() {
    this.$root.$off('getPauseThumbButton')
    this.$root.$off('editPauseThumbButton')
  },
  methods: {
    ...mapMutations('video', ['UPDATE_VIDEO']),
    fixAppendWebsiteVal() {
      this.initialAppendWebsite = this.editingButton.appendWebsiteQueryParams
    },
    handleOpenAppendArticle() {
      const articles = {
        pt: 'https://help.pandavideo.com/pt-br/article/como-enviar-parametros-da-url-para-o-link-do-botao-sj26c',
        en: 'https://help.pandavideo.com/en-us/article/how-to-send-url-parameters-to-the-button-link-1o94tr6',
      }

      window.open(articles[this.$i18next.resolvedLanguage], '_blank')
    },
    blockingEndTime() {
      let endTimeValues = this.editingButton.endTime.split(':').map(Number)
      let startTimeValues = this.editingButton.startTime.split(':').map(Number)

      if (endTimeValues.length === 2) endTimeValues.unshift(0)
      if (startTimeValues.length === 2) startTimeValues.unshift(0)
      let minEndTimeValues = startTimeValues

      if (endTimeValues[1] > startTimeValues[1]) {
        minEndTimeValues[2] = 0
      }
      if (endTimeValues[0] > startTimeValues[0]) {
        minEndTimeValues[1] = 0
      }
      const minEndTimeString = minEndTimeValues
        .map((val) => String(val).padStart(2, '0'))
        .join(':')

      return minEndTimeString
    },
    handleKeepWindow() {
      if (this.editingButton.keepWindow) {
        this.editingButton.keepWindow = false
      }
    },
    handleThumbnailButton() {
      let buttonFormObject = {
        script: this.generateInsideButtonScript(),
        buttonMetadata: this.editingButton,
      }
      this.$root.$emit('buttonsFormInfo', buttonFormObject)
    },
    setConfigCtas() {
      // Ifs com variavel .textButtonColor para verificar a existência do metadata usado pelo layout antigo.
      let videoCtas = this.video?.config?.ctas ?? []
      if (videoCtas[0]?.metadata?.textButtonColor) {
        const mappedCtas = videoCtas.map((cta) => {
          return {
            ...cta,
            metadata: {
              title: cta.title,
              link: cta.metadata.link,
              startTime: this.formatSecondsToTime(cta.time),
              endTime: this.formatSecondsToTime(cta.time + cta.duration),
              selectedButtonType: cta.metadata.isOutsideButton
                ? 'underVideo'
                : 'insideVideo',
              textColor: cta.metadata.textButtonColor,
              textHover: cta.metadata.textButtonColor,
              backgroundColor: cta.metadata.buttonColor,
              backgroundHover: cta.metadata.buttonColor,
              selectedButtonSize: 'small',
              selectedButtonPlacement: 'bottomCenter',
              isCustomHtml: false,
              codeHtml: '',
              lastAllVideo: cta.metadata.lastAllVideo || false,
              lastTillEnd: cta.metadata.lastTillEnd || false,
              keepWindow: cta.metadata.keepWindow || false,
            },
          }
        })
        this.ctaButtons = mappedCtas
      } else {
        videoCtas = videoCtas.filter((el) => el.type === 'video')
        const fixedButtons = videoCtas.map((cta) => {
          return {
            ...cta,
            metadata: {
              ...cta.metadata,
              startTime: this.formatSecondsToTime(cta.time),
              endTime: this.formatSecondsToTime(cta.time + cta.duration),
            },
          }
        })
        this.ctaButtons = fixedButtons
      }
      let videoOutsideCtas = this.video?.config?.outsideCta ?? []
      if (videoOutsideCtas[0]?.metadata?.textButtonColor) {
        const outsideMappedCtas = videoOutsideCtas.map((cta) => {
          return {
            ...cta,
            metadata: {
              title: cta.title,
              link: cta.metadata.link,
              startTime: this.formatSecondsToTime(cta.time),
              endTime: this.formatSecondsToTime(cta.time + cta.duration),
              selectedButtonType: cta.metadata.isOutsideButton
                ? 'underVideo'
                : 'insideVideo',
              textColor: cta.metadata.textButtonColor,
              textHover: cta.metadata.textButtonColor,
              backgroundColor: cta.metadata.buttonColor,
              backgroundHover: cta.metadata.buttonColor,
              selectedButtonSize: 'small',
              selectedButtonPlacement: 'bottomCenter',
              isCustomHtml: cta.metadata.enableCustomHtml,
              codeHtml: cta.metadata.enableCustomHtml ? cta.script : '',
              lastAllVideo: cta.metadata.lastAllVideo || false,
              lastTillEnd: cta.metadata.lastTillEnd || false,
              keepWindow: cta.metadata.keepWindow || false,
            },
          }
        })
        this.outsideCtaButtons = outsideMappedCtas
      } else {
        let fixedButtons = videoOutsideCtas.map((cta) => {
          return {
            ...cta,
            metadata: {
              ...cta.metadata,
              startTime: this.formatSecondsToTime(cta.time),
              endTime: this.formatSecondsToTime(cta.time + cta.duration),
            },
          }
        })
        this.outsideCtaButtons = fixedButtons
      }
    },
    setIframeConfig() {
      this.wasSomethingChanged = true
      if (this.editingButton.selectedButtonType === 'insideVideo') {
        if (this.video.hasOwnProperty('video_source')) {
          this.$root.$emit('setCtas', [this.generateEditingCta()])
        } else {
          const iframe = document.getElementById('panda-player').contentWindow
          iframe.postMessage(
            {
              type: 'ctas',
              parameter: {
                ctas: [this.generateEditingCta()],
              },
            },
            '*'
          )
        }
      }
    },
    generateEditingCta() {
      let time = Number(this.formatTimeToSeconds(this.editingButton.startTime))
      let duration = Number(
        this.formatTimeToSeconds(this.editingButton.endTime) -
          this.formatTimeToSeconds(this.editingButton.startTime)
      )
      if (
        duration < 1 ||
        !this.editingButton.endTime ||
        this.editingButton.endTime === '0' ||
        this.editingButton.lastTillEnd
      ) {
        duration = this.timeVideo
      }
      if (this.editingButton.lastAllVideo) {
        time = 0
        duration = this.timeVideo
      }
      let cta = {
        id: uuidv4(),
        title: this.editingButton.title,
        type: this.buttonType,
        time: time,
        duration: duration,
        script: this.generateInsideButtonScript(),
        metadata: this.editingButton,
      }
      return cta
    },
    handleEditCta(cta) {
      if (cta?.id) {
        this.initialEditingCtaValue = JSON.parse(JSON.stringify(cta))
        this.beingEditedCtaId = cta.id
        this.editingButton = cta.metadata
        this.fixAppendWebsiteVal()
      } else {
        if (this.timeVideo >= 3600) {
          this.editingButton.startTime = '00:00:00'
          this.editingButton.endTime = '00:05:00'
        }
      }
      this.isEditing = true
      this.setIframeConfig()
      this.wasSomethingChanged = false
    },
    async handlePublishNewCta() {
      this.error = ''
      if (this.beingEditedCtaId !== '') {
        return this.handleUpdateCta()
      }
      try {
        this.loadings.creatingCta = true
        const generatedCtas = this.generateCta()

        let ret
        if (this.editingButton.selectedButtonType === 'insideVideo') {
          ret = await playerApi.addCta(this.video.id, generatedCtas)
        } else {
          ret = await playerApi.addOutsideCta(this.video.id, generatedCtas)
        }
        this.video.config = ret.data
        this.UPDATE_VIDEO(this.video)
        this.ctaButtons = ret.data.ctas ?? []
        this.outsideCtaButtons = ret.data.outsideCta ?? []

        this.wasSomethingChanged = false
        this.handleGetBack()
        this.loadings.creatingCta = false
        this.error = ''
        this.initialAppendWebsite = this.editingButton.appendWebsiteQueryParams
      } catch (error) {
        this.error = this.$t('message.pages.video.buttonsPage.errors.addError')
        this.loadings.creatingCta = false
        console.log(error)
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async handleUpdateCta() {
      try {
        this.loadings.updatingCta = true

        let ret
        const initialSelectedType =
          this.initialEditingCtaValue.metadata.selectedButtonType
        const selectedType = this.editingButton.selectedButtonType

        if (selectedType && selectedType === 'insideVideo') {
          if (initialSelectedType && initialSelectedType === 'underVideo') {
            await playerApi.deleteOutsideCta(
              this.video.id,
              this.beingEditedCtaId
            )
          } else {
            await playerApi.deleteCta(this.video.id, this.beingEditedCtaId)
          }
          this.beingEditedCtaId = ''
          let generatedCtas = this.generateCta()
          ret = await playerApi.addCta(this.video.id, generatedCtas)
        } else {
          if (initialSelectedType && initialSelectedType === 'insideVideo') {
            await playerApi.deleteCta(this.video.id, this.beingEditedCtaId)
          } else {
            await playerApi.deleteOutsideCta(
              this.video.id,
              this.beingEditedCtaId
            )
          }

          this.beingEditedCtaId = ''
          const generatedCtas = this.generateCta()
          ret = await playerApi.addOutsideCta(this.video.id, generatedCtas)
        }

        this.video.config = ret.data
        this.UPDATE_VIDEO(this.video)
        this.ctaButtons = ret.data.ctas ? ret.data.ctas : []
        this.outsideCtaButtons = ret.data.outsideCta ? ret.data.outsideCta : []

        this.wasSomethingChanged = false
        this.handleGetBack()

        this.loadings.updatingCta = false
        this.error = ''
      } catch (error) {
        this.error = this.$t(
          'message.pages.video.buttonsPage.errors.updateError'
        )
        this.loadings.updatingCta = false
        console.log(error)
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async handleRemoveCta(cta) {
      try {
        this.loadings.removingCta = true
        this.beingRemovedCtaId = cta.id
        let ret
        if (cta.metadata.selectedButtonType === 'underVideo') {
          ret = await playerApi.deleteOutsideCta(this.video.id, cta.id)
        } else {
          ret = await playerApi.deleteCta(this.video.id, cta.id)
        }
        this.video.config = ret.data
        this.UPDATE_VIDEO(this.video)
        this.ctaButtons = ret.data.ctas ? ret.data.ctas : []
        this.outsideCtaButtons = ret.data.outsideCta ? ret.data.outsideCta : []

        this.loadings.removingCta = false
        this.error = ''
        this.isEditing = this.allCtasVideo.length === 0
      } catch (error) {
        this.error = this.$t(
          'message.pages.video.buttonsPage.errors.removeError'
        )
        this.loadings.removingCta = false
        console.log(error)
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    generateCta() {
      if (!this.beingEditedCtaId || this.beingEditedCtaId === '') {
        const id = uuidv4()
        this.beingEditedCtaId = id
      }
      let time = this.formatTimeToSeconds(this.editingButton.startTime)
      let duration =
        this.formatTimeToSeconds(this.editingButton.endTime) -
        this.formatTimeToSeconds(this.editingButton.startTime)

      if (duration < 1) {
        duration = 0
      }
      if (this.editingButton.lastAllVideo) {
        time = 0
        duration = this.timeVideo
      }
      if (this.editingButton.lastTillEnd) {
        duration = this.timeVideo
      }
      const cta = {
        id: this.beingEditedCtaId,
        title: this.editingButton.title,
        type: 'video',
        time: time,
        duration: duration,
        script: this.editingButton.isCustomHtml
          ? this.editingButton.codeHtml
          : this.generateInsideButtonScript(),
        metadata: this.editingButton,
      }

      if (this.editingButton.isCustomHtml) {
        cta.metadata.appendWebsiteQueryParams = false
      }

      if (cta.metadata.selectedButtonType === 'underVideo') {
        cta['divId'] = this.initialEditingCtaValue?.divId ?? uuidv4()
      }
      return cta
    },
    getButtonPaddings(btnSizeType) {
      let btnPaddings
      let sizes
      if (btnSizeType === 'small') {
        return ''
      }
      if (btnSizeType === 'medium') {
        sizes = {
          width: {
            medium: 700,
            small: 400,
          },
        }
        let measures = [
          { fontSize: '18px', padding: '14px 46px', type: 'medium' },
          { fontSize: '14px', padding: '10px 28px', type: 'small' },
        ]
        btnPaddings = {
          mediaQueries: `
          @media (max-width: ${sizes.width.medium}px) {
            .buttonConfig {
              padding: ${measures[0].padding} !important;
              font-size: ${measures[0].fontSize} !important;
            }
        }
        @media (max-width: ${sizes.width.small}px) {
          .buttonConfig {
            padding: ${measures[1].padding} !important;
            font-size:${measures[1].fontSize} !important;
          }
        }
        `,
        }
      }
      if (btnSizeType === 'large') {
        let sizes = {
          width: {
            medium: 700,
            small: 400,
          },
        }
        let measures = [
          { fontSize: '20px', padding: '18px 56px', type: 'medium' },
          { fontSize: '16px', padding: '14px 36px', type: 'small' },
        ]
        btnPaddings = {
          mediaQueries: `
          @media (max-width: ${sizes.width.medium}px) {
            .buttonConfig {
              padding: ${measures[0].padding} !important;
              font-size: ${measures[0].fontSize} !important;
            }
        }
        @media (max-width: ${sizes.width.small}px) {
          .buttonConfig {
            padding: ${measures[1].padding} !important;
            font-size:${measures[1].fontSize} !important;
          }
        }
        `,
        }
      }
      return btnPaddings
    },
    generateInsideButtonScript(placementFlag) {
      let buttonConfig = this.editingButton
      let btnAlignment
      let btnType = buttonConfig.selectedButtonPlacement.toLowerCase()
      let btnQueries
      if (buttonConfig.selectedButtonType === 'underVideo') {
        btnAlignment = 'justify-content:center; align-items: center'
      } else {
        if (btnType.includes('left')) btnAlignment = 'justify-content:left'
        if (btnType.includes('center')) btnAlignment = 'justify-content:center'
        if (btnType.includes('right')) btnAlignment = 'justify-content:right'
        if (btnType.includes('top')) btnAlignment += ';align-items:start'
        if (btnType.includes('mid')) btnAlignment += ';align-items:center'
        if (btnType.includes('bottom')) btnAlignment += ';align-items:end'
      }
      let btnMeasurements
      let btnSizeType = buttonConfig.selectedButtonSize
      if (btnSizeType === 'small')
        btnMeasurements = 'padding: 18px 54px;font-size:16px;'
      if (btnSizeType === 'medium')
        btnMeasurements = 'padding: 22px 66px;font-size:22px;'
      if (btnSizeType === 'large')
        btnMeasurements = 'padding: 26px 78px;font-size:26px;'
      if (btnSizeType === 'medium' || btnSizeType === 'large') {
        let paddings = this.getButtonPaddings(btnSizeType)
        btnQueries = paddings.mediaQueries
      }
      let scriptMargin
      if (btnType.includes('topleft')) scriptMargin = 'margin:30px'
      if (btnType.includes('topcenter')) scriptMargin = 'margin:30px'
      if (btnType.includes('topright')) scriptMargin = 'margin:30px'
      if (btnType.includes('midleft')) scriptMargin = 'margin: 0px 30px'
      if (btnType.includes('midcenter')) scriptMargin = 'margin: 0px'
      if (btnType.includes('midright')) scriptMargin = 'margin: 0px 30px'
      if (btnType.includes('bottomleft')) scriptMargin = 'margin: 75px 30px'
      if (btnType.includes('bottomcenter')) scriptMargin = 'margin: 75px 30px'
      if (btnType.includes('bottomright')) scriptMargin = 'margin: 75px 30px'
      if (buttonConfig.selectedButtonType === 'underVideo') scriptMargin = ''
      if (placementFlag) scriptMargin = 'margin: 30px 0px 10px 0px'
      /* eslint-disable quotes */
      let generatedScript =
        `${
          placementFlag === 'outsideBtn'
            ? ''
            : `<script>function pause(){window.parent.postMessage({type: 'pause',}, '*')};
        function redirect(){window.open('${buttonConfig.link}')};
        document.getElementById("button-container").addEventListener("click", function(e) {if (e.target == this) {window.parent.postMessage({type: 'play_toggle',}, '*')}})
        </scr` + 'ipt>'
        }<st` +
        `yle> .buttonConfig { cursor:pointer; ${
          scriptMargin && scriptMargin
        }; background-color:${buttonConfig.backgroundColor};
        color:${
          buttonConfig.textColor
        }; ${btnMeasurements}; border:none; border-radius:10px;}
        .buttonConfig:hover {background-color:${
          buttonConfig.backgroundHover
        };color:${buttonConfig.textHover}}
${btnQueries ?? ''}
        </style>
        <div id="button-container" style="display:flex;${btnAlignment};height:100%;${
          this.isThumbButton
            ? "background-color: #000;background-position: 50%;background-repeat: no-repeat;background-size: contain;background-image: url('" +
              this.pauseThumbUrl +
              "')" // eslint-disable-line quotes
            : ''
        }">
        <button class="buttonConfig" onclick="pause()&redirect()"><b>${
          buttonConfig.title
        }</b></button></div>`
      /* eslint-enable quotes */
      return generatedScript
    },
    handleLastAllVideo() {
      if (this.editingButton.lastAllVideo) {
        this.editingButton.startTime = this.getDefaultTimes().start
        this.editingButton.endTime = this.formatSecondsToTime(this.timeVideo)
        this.editingButton.lastTillEnd = false
        this.setIframeConfig()
      }
    },
    handleLastTillEnd() {
      if (this.editingButton.lastTillEnd) {
        this.editingButton.endTime = this.formatSecondsToTime(this.timeVideo)
        this.editingButton.lastAllVideo = false
        this.setIframeConfig()
      }
    },
    handleChangeButtonType(type) {
      this.wasSomethingChanged = true
      this.editingButton.selectedButtonType = type
    },
    handleChangeButtonPlacement(placement) {
      this.editingButton.selectedButtonPlacement = placement
      this.setIframeConfig()
    },
    handleCancelEditing() {
      this.isEditing = false
      this.resetFormData()
    },
    onScroll() {
      this.scrollInvoked++
    },
    handleGetBack() {
      this.beingEditedCtaId = ''
      if (this.wasSomethingChanged) {
        this.$root.$emit('unsavedChangesModal', true)
        return
      }
      if (this.isEditing && this.allCtasVideo.length !== 0) {
        this.isEditing = false
        this.resetFormData()
      } else {
        this.handleBack()
      }
    },
    handleOpenCodeModal(cta) {
      this.$root.$emit('ctaModal', cta, false, this.video, this.video.video_hls)
    },
    resetFormData() {
      this.initialEditingCtaValue = {}
      this.beingEditedCtaId = ''
      this.editingButton = {
        title: 'Acesse aqui',
        link: 'https://',
        startTime: this.getDefaultTimes().start,
        endTime: this.getDefaultTimes().end,
        selectedButtonType: 'underVideo',
        textColor: '#FFFFFF',
        textHover: '#FFFFFF',
        backgroundColor: '#3e50f7',
        backgroundHover: '#3e50f7',
        selectedButtonSize: 'small',
        selectedButtonPlacement: 'bottomCenter',
        isCustomHtml: false,
        codeHtml: '',
        lastAllVideo: false,
        lastTillEnd: false,
      }
      this.initialAppendWebsite = this.editingButton.appendWebsiteQueryParams
      this.wasSomethingChanged = false
    },
    formatSecondsToTime(seconds) {
      let res = new Date(seconds * 1000).toISOString()
      if (this.timeVideo < 3600) {
        return res.substring(14, 19)
      }
      return res.substring(11, 19)
    },
    formatTimeToSeconds(time) {
      let res = time.split(':').reduce((acc, time) => 60 * acc + +time)
      return res
    },
    getDefaultTimes() {
      if (this.timeVideo && this.timeVideo >= 3600) {
        return {
          start: '00:00:00',
          end: '00:05:00',
        }
      }
      return {
        start: '00:00',
        end: '00:05',
      }
    },
  },
}
</script>
